import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Section from '../components/Sections/Section'
import ProjectCard from '../components/Cards/ProjectCard/ProjectCard';
import { generalMouseMoveAnimation, generalMouseOutAnimation } from '../components/Cards/ServiceCard/ServiceCardAnimation';

gsap.registerPlugin(ScrollTrigger);

export const PreloadProjectCards = async (databaseId) => {
    const proxyUrl = `https://flintandsteel.io/notion-proxy-database.php?databaseId=${databaseId}`;
    try {
        const response = await fetch(proxyUrl);
        if (!response.ok) {
            throw new Error(`Error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        const formattedPages = data.results
            .filter(page => page.properties.Status?.select?.name === 'Live')
            .map(page => {
                const title = page.properties.Name.title?.[0]?.plain_text || 'Untitled';
                const description = page.properties.Description?.rich_text?.[0]?.plain_text || 'No description available';
                const coverPhoto = page.cover ? page.cover.external?.url || '' : '';
                const pageId = page.id;
                return { title, description, coverPhoto, pageId };
            });

        localStorage.setItem(`notionData_${databaseId}`, JSON.stringify(formattedPages));
    } catch (err) {
        console.error('Error preloading project cards:', err);
    }
};

const NotionProjectCards = ({ databaseId }) => {
    const [pages, setPages] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const proxyUrl = `https://flintandsteel.io/notion-proxy-database.php?databaseId=${databaseId}`;
    const cardRefs = useRef([]);
    const containerRef = useRef(null);
    const animationTriggeredRef = useRef(false);

    useEffect(() => {
        const retrievePages = async () => {
            try {
                const cachedData = localStorage.getItem(`notionData_${databaseId}`);
                if (cachedData) {
                    setPages(JSON.parse(cachedData));
                    setIsLoading(false);
                }

                const response = await fetch(proxyUrl);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`);
                }
                const data = await response.json();

                const formattedPages = data.results
                .filter(page => page.properties.Status?.select?.name === 'Live')
                .map(page => {
                    const title = page.properties.Name.title[0]?.plain_text || 'Untitled';
                    const description = page.properties.Description?.rich_text[0]?.plain_text || 'No description available';
                    const coverPhoto = page.cover?.type === 'file' 
                        ? page.cover.file.url 
                        : page.cover?.external?.url || '';
                    const pageId = page.id;
                    
                    return { title, description, coverPhoto, pageId };
                });

                localStorage.setItem(`notionData_${databaseId}`, JSON.stringify(formattedPages));

                setPages(formattedPages);
                setIsLoading(false);
            } catch (err) {
                console.error('Error retrieving pages:', err);
                setError(err.message);
                setIsLoading(false);
            }
        };
    
        retrievePages();
    }, [databaseId, proxyUrl]);    

    useEffect(() => {
        cardRefs.current = cardRefs.current.slice(0, pages.length);
    }, [pages]);

    useEffect(() => {
        if (!isLoading && pages.length > 0 && containerRef.current && !animationTriggeredRef.current) {
            animateProjectCards();
            animationTriggeredRef.current = true;
        }
    }, [isLoading, pages]);

    const animateProjectCards = () => {
        const cards = containerRef.current.querySelectorAll('.project-card');
        
        cards.forEach((card, index) => {
            gsap.fromTo(
                card,
                { 
                    autoAlpha: 0, 
                    y: 20,
                    scale: 0.95
                },
                {
                    autoAlpha: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.2,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top bottom-=50',
                        toggleActions: 'play none none reverse',
                    },
                    delay: index * 0.2,
                }
            );
        });
    };

    const handleMouseEnter = (index) => {
        generalMouseMoveAnimation(cardRefs.current[index]);
    };

    const handleMouseLeave = (index) => {
        generalMouseOutAnimation(cardRefs.current[index]);
    };

    if (error) {
        return <div>Foutmelding: {error}</div>;
    }

    return (
        <Section className="grid--4 portfolio-content" ref={containerRef}>
            {!isLoading && pages.length > 0 ? (
                pages.map(({ title, description, coverPhoto, pageId }, index) => (
                    <div
                        key={pageId}
                        ref={el => cardRefs.current[index] = el}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={() => handleMouseLeave(index)}
                    >
                        <ProjectCard
                            title={title}
                            description={description}
                            coverPhoto={coverPhoto}
                            pageId={pageId}
                        />
                    </div>
                ))
            ) : (
                <p>Laden...</p>
            )}
        </Section>
    );
};

export default NotionProjectCards;
